import { type intlFormatPropTypes } from "react-intl";
import { z } from "zod";

// Imported per-module rather than through the kit's barrel: the barrel re-exports
// `AddressFields`, which pulls in the design system, and that cannot be resolved
// from a schema test.
import { addressFormMessages } from "../../AddressForm/AddressForm.messages";
import type { Country } from "../../AddressForm/AddressForm.types";
import {
  createAddressRules,
  isBlank,
} from "../../AddressForm/AddressForm.utils";

import {
  BILLING_ADDRESS_FIELDS,
  BILLING_CHOICE,
  SPACE_ADDRESS_FIELDS,
} from "./AddSpaceAddressForm.consts";

/**
 * Shape only — the rules live in `createAddressFormSchema`, since most depend on the
 * selected country. Names are mapped onto the API payload in
 * `AddSpaceAddressForm.submit` (`spaceStreet` -> `street`), so they cannot be
 * renamed on their own.
 */
const addressFormShape = z.object({
  country: z.string(),
  spaceStreet: z.string(),
  spaceUnit: z.string(),
  spaceCity: z.string(),
  spaceState: z.string(),
  spaceZip: z.string(),
  billingChoice: z.enum([
    BILLING_CHOICE.SameAsSpace,
    BILLING_CHOICE.DifferentAddress,
  ]),
  billingStreet: z.string(),
  billingUnit: z.string(),
  billingCity: z.string(),
  billingState: z.string(),
  billingZip: z.string(),
});

type AddressFormValues = z.infer<typeof addressFormShape>;

type CreateAddressFormSchemaArgs = {
  intl: Pick<intlFormatPropTypes, "formatMessage">;
  /** The country currently picked in the form, from `@shopify/address`. */
  selectedCountry?: Country;
};

/**
 * A factory, not a constant: country choice decides whether a zone is required, what
 * the zone/city/postal fields are called, and which postal formats are valid, so the
 * form rebuilds this whenever the selection changes.
 *
 * Billing is validated **only** for "use a different address", so a hidden billing
 * block never blocks submit.
 */
const createAddressFormSchema = ({
  intl,
  selectedCountry,
}: CreateAddressFormSchemaArgs) =>
  addressFormShape.superRefine((values, ctx) => {
    // Checked here rather than in `createAddressRules`: one country field serves both
    // addresses, so per-address checks would report a missing country twice.
    if (isBlank(values.country)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["country"],
        message: intl.formatMessage(addressFormMessages.selectCountry),
      });
    }

    createAddressRules({
      fields: SPACE_ADDRESS_FIELDS,
      intl,
      selectedCountry,
    })(values, ctx);

    if (values.billingChoice === BILLING_CHOICE.DifferentAddress) {
      createAddressRules({
        fields: BILLING_ADDRESS_FIELDS,
        intl,
        selectedCountry,
      })(values, ctx);
    }
  });

export { createAddressFormSchema };

export type { AddressFormValues };
